/**
 * Comparación de texto para los buscadores de la app.
 *
 * Quien busca a una persona escribe lo que recuerda: "nunez", "NÚÑEZ", o el
 * apellido materno antes que el paterno. Comparar con `includes` sobre el texto
 * crudo falla en los tres casos, así que se normaliza y se busca por palabras.
 */

/** Marcas diacríticas que deja `NFD` al descomponer (tildes, diéresis, virgulilla). */
const DIACRITICOS = /[̀-ͯ]/g;

/**
 * Texto comparable: sin tildes, en minúscula y con los espacios colapsados.
 *
 * `NFD` parte "ñ" en "n" + virgulilla, así que la ñ termina como n. Es a
 * propósito: quien no tiene el teclado a mano escribe "nunez" y espera encontrar
 * a Núñez.
 */
export function normalizar(texto: string): string {
  return (texto ?? "")
    .normalize("NFD")
    .replace(DIACRITICOS, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * `true` si el texto contiene TODAS las palabras de la búsqueda, en cualquier orden.
 *
 * Buscar por palabras y no por la frase entera es lo que hace que "nunez carla"
 * encuentre a "Carla Elena Núñez Pardo": el orden de nombres y apellidos que uno
 * recuerda casi nunca es el que está guardado.
 *
 * Una búsqueda vacía calza con todo (no filtra).
 */
export function coincide(texto: string, busqueda: string): boolean {
  const palabras = normalizar(busqueda).split(" ").filter(Boolean);
  if (!palabras.length) return true;
  const objetivo = normalizar(texto);
  return palabras.every((palabra) => objetivo.includes(palabra));
}
